import "@/App.css";
import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'sonner';
import { authService } from './lib/auth';
import VersionCheck from './components/VersionCheck';
import Login from './pages/Login';
import Register from './pages/Register';
import Dashboard from './pages/Dashboard';
import SalesDashboard from './pages/SalesDashboard';
import SalesPerformance from './pages/SalesPerformance';
import Leads from './pages/Leads';
import UnusedLeads from './pages/UnusedLeads';
import Users from './pages/Users';
import Reminders from './pages/Reminders';
import GoogleSheetSync from './pages/GoogleSheetSync';
import CallerGroups from './pages/CallerGroups';
import LeadGroups from './pages/LeadGroups';
import Products from './pages/Products';
import Leaderboard from './pages/Leaderboard';
import ProductWiseSales from './pages/ProductWiseSales';
import ModuleAccess from './pages/ModuleAccess';
import ComplaintsDashboard from './pages/ComplaintsDashboard';
import ComplaintPerformance from './pages/ComplaintPerformance';
import Complaints from './pages/Complaints';
import NewComplaint from './pages/NewComplaint';
import ProblemTypes from './pages/ProblemTypes';
import ClassDashboard from './pages/ClassDashboard';
import Classes from './pages/Classes';
import Teachers from './pages/Teachers';
import Subjects from './pages/Subjects';
import TeacherReports from './pages/TeacherReports';
import TeacherPerformance from './pages/TeacherPerformance';
// import CoursePlanner from './pages/CoursePlanner'; // Temporarily disabled
import CourseProgress from './pages/CourseProgress';
import ClassTyping from './pages/ClassTyping';
import Content from './pages/Content';
import PasswordResetRequests from './pages/PasswordResetRequests';
import StockInwardOutward from './pages/StockInwardOutward';
import StockStatus from './pages/StockStatus';
import StockModify from './pages/StockModify';
import PaperStock from './pages/PaperStock';
import BookLedger from './pages/BookLedger';
import HRDashboard from './pages/HRDashboard';
import HREmployees from './pages/HREmployees';
import HRSalary from './pages/HRSalary';
import HRDocuments from './pages/HRDocuments';
import FinanceDashboard from './pages/FinanceDashboard';
import AttendanceTeams from './pages/AttendanceTeams';
import AttendanceMarking from './pages/AttendanceMarking';
import AttendanceReports from './pages/AttendanceReports';
import SalesCompetitions from './pages/SalesCompetitions';
import OfflineEnquiries from './pages/OfflineEnquiries';
import OfflineAdmissions from './pages/OfflineAdmissions';
import OfflineBatches from './pages/OfflineBatches';
import OfflineClasses from './pages/OfflineClasses';
import OfflineReports from './pages/OfflineReports';
import OfflineDashboard from './pages/OfflineDashboard';
import OldFeeCollection from './pages/OldFeeCollection';
import SpecialEvents from './pages/SpecialEvents';
import SpecialEventsTeacherStats from './pages/SpecialEventsTeacherStats';
import Marketing from './pages/WhatsAppMarketing';
import CashFlow from './pages/CashFlow';
import YouTubeClassesByChannel from './pages/YouTubeClassesByChannel';
import TaskManager from './pages/TaskManager';
import Results from './pages/Results';
import Ecommerce from './pages/Ecommerce';
import LeadForms from './pages/LeadForms';
import PublicLeadForm from './pages/PublicLeadForm';
import PublicComplaint from './pages/PublicComplaint';
import ComplaintFormSettings from './pages/ComplaintFormSettings';
import ResultForms from './pages/ResultForms';
import PublicResultForm from './pages/PublicResultForm';
import DoubtEngineAdmin from './pages/DoubtEngineAdmin';
import DoubtEngineTeacher from './pages/DoubtEngineTeacher';
import PublicDoubtEngine from './pages/PublicDoubtEngine';
import TeacherFeedbackAdmin from './pages/TeacherFeedbackAdmin';
import TeacherFeedbackMine from './pages/TeacherFeedbackMine';
import PublicTeacherFeedback from './pages/PublicTeacherFeedback';
import PublicComplaintChat from './pages/PublicComplaintChat';
import ComplaintChatAgent from './pages/ComplaintChatAgent';
import ComplaintChatAdmin from './pages/ComplaintChatAdmin';
import Layout from './components/Layout';
import ProtectedRoute from './components/ProtectedRoute';

function App() {
  return (
    <BrowserRouter>
      <Toaster position="top-right" richColors />
      <VersionCheck />
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route path="/register" element={<Register />} />
        <Route path="/f/:slug" element={<PublicLeadForm />} />
        <Route path="/complaint" element={<PublicComplaint />} />
        <Route path="/result/:slug" element={<PublicResultForm />} />
        <Route path="/doubt/:slug" element={<PublicDoubtEngine />} />
        <Route path="/feedback/:slug" element={<PublicTeacherFeedback />} />
        <Route path="/complaint/chat/:chatId" element={<PublicComplaintChat />} />
        <Route
          path="/"
          element={
            authService.isAuthenticated() ? (
              <Navigate to="/dashboard" replace />
            ) : (
              <Navigate to="/login" replace />
            )
          }
        />
        <Route
          path="/"
          element={
            <ProtectedRoute>
              <Layout />
            </ProtectedRoute>
          }
        >
          {/* Main Dashboard - Admin only */}
          <Route 
            path="dashboard" 
            element={
              <ProtectedRoute allowedRoles={['admin']}>
                <Dashboard />
              </ProtectedRoute>
            } 
          />
          
          {/* Sales Calling Module */}
          <Route 
            path="sales/dashboard" 
            element={
              <ProtectedRoute allowedRoles={['admin', 'manager']}>
                <SalesDashboard />
              </ProtectedRoute>
            }
          />
          <Route 
            path="sales/my-performance" 
            element={
              <ProtectedRoute allowedRoles={['caller']}>
                <SalesPerformance />
              </ProtectedRoute>
            }
          />
          <Route 
            path="sales/competitions" 
            element={
              <ProtectedRoute allowedRoles={['admin', 'manager']}>
                <SalesCompetitions />
              </ProtectedRoute>
            }
          />
          <Route path="sales/leads" element={<Leads />} />
          <Route
            path="sales/reminders"
            element={
              <ProtectedRoute allowedRoles={['caller']}>
                <Reminders />
              </ProtectedRoute>
            }
          />
          <Route
            path="sales/users"
            element={
              <ProtectedRoute allowedRoles={['admin', 'manager']}>
                <Users />
              </ProtectedRoute>
            }
          />
          <Route
            path="sales/google-sheet-sync"
            element={
              <ProtectedRoute allowedRoles={['admin', 'manager']}>
                <GoogleSheetSync />
              </ProtectedRoute>
            }
          />
          <Route
            path="sales/caller-groups"
            element={
              <ProtectedRoute allowedRoles={['admin', 'manager']}>
                <CallerGroups />
              </ProtectedRoute>
            }
          />
          <Route
            path="sales/lead-groups"
            element={
              <ProtectedRoute allowedRoles={['admin', 'manager']}>
                <LeadGroups />
              </ProtectedRoute>
            }
          />
          <Route
            path="sales/unused-leads"
            element={
              <ProtectedRoute allowedRoles={['admin', 'manager']}>
                <UnusedLeads />
              </ProtectedRoute>
            }
          />
          <Route
            path="sales/products"
            element={
              <ProtectedRoute allowedRoles={['admin', 'manager']}>
                <Products />
              </ProtectedRoute>
            }
          />
          <Route
            path="sales/lead-forms"
            element={
              <ProtectedRoute allowedRoles={['admin', 'manager']}>
                <LeadForms />
              </ProtectedRoute>
            }
          />
          <Route
            path="sales/leaderboard"
            element={
              <ProtectedRoute allowedRoles={['admin', 'manager', 'caller']}>
                <Leaderboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="sales/product-wise"
            element={
              <ProtectedRoute allowedRoles={['admin', 'manager']}>
                <ProductWiseSales />
              </ProtectedRoute>
            }
          />

          {/* Complaints Module */}
          <Route
            path="complaints/dashboard"
            element={
              <ProtectedRoute allowedRoles={['admin', 'manager']}>
                <ComplaintsDashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="complaints/my-performance"
            element={
              <ProtectedRoute allowedRoles={['complaint_entry', 'complaint_solver']}>
                <ComplaintPerformance />
              </ProtectedRoute>
            }
          />
          <Route
            path="complaints/new"
            element={
              <ProtectedRoute allowedRoles={['admin', 'manager', 'complaint_entry']}>
                <NewComplaint />
              </ProtectedRoute>
            }
          />
          <Route
            path="complaints/list"
            element={
              <ProtectedRoute allowedRoles={['admin', 'manager', 'complaint_entry', 'complaint_solver']}>
                <Complaints />
              </ProtectedRoute>
            }
          />
          <Route
            path="complaints/problem-types"
            element={
              <ProtectedRoute allowedRoles={['admin']}>
                <ProblemTypes />
              </ProtectedRoute>
            }
          />
          <Route
            path="complaints/public-form"
            element={
              <ProtectedRoute allowedRoles={['admin', 'manager']}>
                <ComplaintFormSettings />
              </ProtectedRoute>
            }
          />

          {/* Admin Module */}
          <Route
            path="admin/module-access"
            element={
              <ProtectedRoute allowedRoles={['admin']}>
                <ModuleAccess />
              </ProtectedRoute>
            }
          />
          <Route
            path="admin/users"
            element={
              <ProtectedRoute allowedRoles={['admin', 'manager']}>
                <Users />
              </ProtectedRoute>
            }
          />
          <Route
            path="admin/password-resets"
            element={
              <ProtectedRoute allowedRoles={['admin', 'manager']}>
                <PasswordResetRequests />
              </ProtectedRoute>
            }
          />

          {/* Class Delivery Module */}
          {/* Redirect /classes to /classes/app */}
          <Route
            path="classes"
            element={<Navigate to="/classes/app" replace />}
          />
          <Route
            path="classes/dashboard"
            element={
              <ProtectedRoute allowedRoles={['admin', 'manager', 'class_conduction_agent']}>
                <ClassDashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="classes/my-performance"
            element={
              <ProtectedRoute allowedRoles={['teacher']}>
                <TeacherPerformance />
              </ProtectedRoute>
            }
          />
          <Route
            path="classes/list"
            element={<Navigate to="/classes/app" replace />}
          />
          <Route
            path="classes/app"
            element={
              <ProtectedRoute allowedRoles={['admin', 'manager', 'class_conduction_agent', 'teacher']}>
                <Classes classType="app" />
              </ProtectedRoute>
            }
          />
          <Route
            path="classes/youtube"
            element={
              <ProtectedRoute allowedRoles={['admin', 'manager', 'class_conduction_agent', 'teacher']}>
                <Classes classType="youtube" />
              </ProtectedRoute>
            }
          />
          <Route
            path="classes/youtube/by-channel"
            element={
              <ProtectedRoute allowedRoles={['admin', 'manager', 'class_conduction_agent', 'teacher']}>
                <YouTubeClassesByChannel />
              </ProtectedRoute>
            }
          />
          <Route
            path="classes/teachers"
            element={
              <ProtectedRoute allowedRoles={['admin', 'manager']}>
                <Teachers />
              </ProtectedRoute>
            }
          />
          <Route
            path="classes/subjects"
            element={
              <ProtectedRoute allowedRoles={['admin', 'manager']}>
                <Subjects />
              </ProtectedRoute>
            }
          />
          <Route
            path="classes/reports"
            element={
              <ProtectedRoute allowedRoles={['admin', 'manager']}>
                <TeacherReports />
              </ProtectedRoute>
            }
          />
          {/* Course Progress Report */}
          <Route
            path="classes/progress"
            element={
              <ProtectedRoute allowedRoles={['admin', 'manager', 'class_conduction_agent', 'teacher']}>
                <CourseProgress />
              </ProtectedRoute>
            }
          />
          
          {/* Typing Module - Sub-routes */}
          <Route
            path="typing/repository"
            element={
              <ProtectedRoute allowedRoles={['admin', 'typing_manager', 'typing_agent', 'teacher', 'manager']}>
                <ClassTyping initialTab="repository" />
              </ProtectedRoute>
            }
          />
          <Route
            path="typing/agents"
            element={
              <ProtectedRoute allowedRoles={['admin', 'typing_manager', 'manager']}>
                <ClassTyping initialTab="agents" />
              </ProtectedRoute>
            }
          />
          {/* Default typing route - redirect to repository */}
          <Route
            path="typing"
            element={
              <ProtectedRoute allowedRoles={['admin', 'typing_manager', 'typing_agent', 'teacher', 'manager']}>
                <ClassTyping initialTab="repository" />
              </ProtectedRoute>
            }
          />

          {/* Content Module - Sub-routes */}
          <Route
            path="content/questions"
            element={
              <ProtectedRoute allowedRoles={['admin', 'content_manager', 'content_agent', 'manager']}>
                <Content initialTab="questions" />
              </ProtectedRoute>
            }
          />
          <Route
            path="content/books"
            element={
              <ProtectedRoute allowedRoles={['admin', 'content_manager', 'content_agent', 'manager']}>
                <Content initialTab="books" />
              </ProtectedRoute>
            }
          />
          <Route
            path="content/subjects"
            element={
              <ProtectedRoute allowedRoles={['admin', 'content_manager', 'manager']}>
                <Content initialTab="subjects" />
              </ProtectedRoute>
            }
          />
          {/* Default content route - redirect to questions */}
          <Route
            path="content"
            element={
              <ProtectedRoute allowedRoles={['admin', 'content_manager', 'content_agent', 'manager']}>
                <Content initialTab="questions" />
              </ProtectedRoute>
            }
          />

          {/* Books Stock Module */}
          <Route
            path="stock/inward-outward"
            element={
              <ProtectedRoute allowedRoles={['admin', 'manager', 'stock_manager']}>
                <StockInwardOutward />
              </ProtectedRoute>
            }
          />
          <Route
            path="stock/status"
            element={
              <ProtectedRoute allowedRoles={['admin', 'manager', 'stock_manager']}>
                <StockStatus />
              </ProtectedRoute>
            }
          />
          <Route
            path="stock/modify"
            element={
              <ProtectedRoute allowedRoles={['admin', 'manager', 'stock_manager']}>
                <StockModify />
              </ProtectedRoute>
            }
          />
          <Route
            path="stock/paper"
            element={
              <ProtectedRoute allowedRoles={['admin']}>
                <PaperStock />
              </ProtectedRoute>
            }
          />
          <Route
            path="stock/ledger"
            element={
              <ProtectedRoute allowedRoles={['admin', 'manager', 'stock_manager']}>
                <BookLedger />
              </ProtectedRoute>
            }
          />

          {/* HR Module - Admin and Managers with HR access */}
          <Route
            path="hr/dashboard"
            element={
              <ProtectedRoute allowedRoles={['admin', 'hr_manager', 'manager']}>
                <HRDashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="hr/employees"
            element={
              <ProtectedRoute allowedRoles={['admin', 'hr_manager', 'manager']}>
                <HREmployees />
              </ProtectedRoute>
            }
          />
          <Route
            path="hr/salary"
            element={
              <ProtectedRoute allowedRoles={['admin', 'hr_manager', 'manager']}>
                <HRSalary />
              </ProtectedRoute>
            }
          />
          <Route
            path="hr/documents"
            element={
              <ProtectedRoute allowedRoles={['admin', 'hr_manager', 'manager']}>
                <HRDocuments />
              </ProtectedRoute>
            }
          />

          {/* Attendance Management - Admin, Managers with HR access, and Team Leaders */}
          <Route
            path="hr/attendance/teams"
            element={
              <ProtectedRoute allowedRoles={['admin', 'hr_manager', 'manager']}>
                <AttendanceTeams />
              </ProtectedRoute>
            }
          />
          <Route
            path="hr/attendance/mark"
            element={
              <ProtectedRoute allowedRoles={['admin', 'hr_manager', 'manager', 'caller', 'teacher', 'class_conduction_agent', 'complaint_entry', 'complaint_solver', 'stock_manager']}>
                <AttendanceMarking />
              </ProtectedRoute>
            }
          />
          <Route
            path="hr/attendance/reports"
            element={
              <ProtectedRoute allowedRoles={['admin', 'hr_manager', 'manager']}>
                <AttendanceReports />
              </ProtectedRoute>
            }
          />

          {/* Finance Module - Admin and Managers with Finance access */}
          <Route
            path="finance/dashboard"
            element={
              <ProtectedRoute allowedRoles={['admin', 'manager']}>
                <FinanceDashboard />
              </ProtectedRoute>
            }
          />

          {/* Offline Centre Module */}
          <Route
            path="offline/dashboard"
            element={
              <ProtectedRoute allowedRoles={['admin', 'manager', 'offline_manager', 'counsellor']}>
                <OfflineDashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="offline/enquiries"
            element={
              <ProtectedRoute allowedRoles={['admin', 'manager', 'offline_manager', 'counsellor']}>
                <OfflineEnquiries />
              </ProtectedRoute>
            }
          />
          <Route
            path="offline/admissions"
            element={
              <ProtectedRoute allowedRoles={['admin', 'manager', 'offline_manager', 'counsellor']}>
                <OfflineAdmissions />
              </ProtectedRoute>
            }
          />
          <Route
            path="offline/batches"
            element={
              <ProtectedRoute allowedRoles={['admin', 'manager', 'offline_manager']}>
                <OfflineBatches />
              </ProtectedRoute>
            }
          />
          <Route
            path="offline/classes"
            element={
              <ProtectedRoute allowedRoles={['admin', 'manager', 'offline_manager']}>
                <OfflineClasses />
              </ProtectedRoute>
            }
          />
          <Route
            path="offline/reports"
            element={
              <ProtectedRoute allowedRoles={['admin', 'manager', 'offline_manager']}>
                <OfflineReports />
              </ProtectedRoute>
            }
          />
          <Route
            path="offline/old-fee"
            element={
              <ProtectedRoute allowedRoles={['admin', 'manager', 'offline_manager', 'counsellor']}>
                <OldFeeCollection />
              </ProtectedRoute>
            }
          />

          {/* Special Events */}
          <Route
            path="special-events"
            element={
              <ProtectedRoute allowedRoles={['admin', 'manager', 'special_events_agent']} requiredModule="special_events">
                <SpecialEvents mode="scheduling" />
              </ProtectedRoute>
            }
          />
          <Route
            path="special-events/customization"
            element={
              <ProtectedRoute allowedRoles={['admin', 'manager', 'special_events_agent']} requiredModule="special_events">
                <SpecialEvents mode="library" />
              </ProtectedRoute>
            }
          />
          <Route
            path="special-events/teacher-stats"
            element={
              <ProtectedRoute allowedRoles={['admin', 'manager']}>
                <SpecialEventsTeacherStats />
              </ProtectedRoute>
            }
          />
          <Route
            path="tasks"
            element={
              <ProtectedRoute allowedRoles={['admin', 'manager']} requiredModule="task_manager">
                <TaskManager />
              </ProtectedRoute>
            }
          />
          <Route
            path="results"
            element={
              <ProtectedRoute allowedRoles={['admin', 'manager', 'result_agent']} requiredModule="results">
                <Results />
              </ProtectedRoute>
            }
          />
          <Route
            path="results/forms"
            element={
              <ProtectedRoute allowedRoles={['admin', 'manager']} requiredModule="results">
                <ResultForms />
              </ProtectedRoute>
            }
          />

          <Route
            path="ecommerce"
            element={
              <ProtectedRoute allowedRoles={['admin', 'manager']} requiredModule="ecommerce">
                <Ecommerce />
              </ProtectedRoute>
            }
          />

          {/* Marketing */}
          <Route
            path="marketing"
            element={
              <ProtectedRoute allowedRoles={['admin', 'manager', 'marketing_agent']} requiredModule="marketing">
                <Marketing />
              </ProtectedRoute>
            }
          />

          {/* Cash Flow */}
          <Route
            path="cashflow"
            element={
              <ProtectedRoute allowedRoles={['admin', 'manager']} requiredModule="cash_flow">
                <CashFlow />
              </ProtectedRoute>
            }
          />
          <Route
            path="cashflow/:section"
            element={
              <ProtectedRoute allowedRoles={['admin', 'manager']} requiredModule="cash_flow">
                <CashFlow />
              </ProtectedRoute>
            }
          />

          {/* Doubt Engine */}
          <Route
            path="doubt-engine"
            element={
              <ProtectedRoute allowedRoles={['admin', 'manager', 'doubt_engine_manager']} requiredModule="doubt_engine">
                <DoubtEngineAdmin />
              </ProtectedRoute>
            }
          />
          <Route
            path="doubt-engine/inbox"
            element={
              <ProtectedRoute allowedRoles={['teacher', 'admin', 'manager', 'doubt_engine_manager']} requiredModule="doubt_engine">
                <DoubtEngineTeacher />
              </ProtectedRoute>
            }
          />

          {/* Teacher Feedback */}
          <Route
            path="teacher-feedback"
            element={
              <ProtectedRoute allowedRoles={['admin', 'manager', 'feedback_manager']} requiredModule="teacher_feedback">
                <TeacherFeedbackAdmin />
              </ProtectedRoute>
            }
          />
          <Route
            path="teacher-feedback/mine"
            element={
              <ProtectedRoute allowedRoles={['teacher', 'admin', 'manager', 'feedback_manager']} requiredModule="teacher_feedback">
                <TeacherFeedbackMine />
              </ProtectedRoute>
            }
          />

          {/* Complaint Live Chat */}
          <Route
            path="complaint-chat"
            element={
              <ProtectedRoute allowedRoles={['admin', 'manager']} requiredModule="complaint_chat">
                <ComplaintChatAdmin />
              </ProtectedRoute>
            }
          />
          <Route
            path="complaint-chat/agent"
            element={
              <ProtectedRoute allowedRoles={['admin', 'manager', 'complaint_solver', 'complaint_entry']} requiredModule="complaint_chat">
                <ComplaintChatAgent />
              </ProtectedRoute>
            }
          />

          {/* Legacy routes - redirect to new paths */}
          <Route path="leads" element={<Navigate to="/sales/leads" replace />} />
          <Route path="reminders" element={<Navigate to="/sales/reminders" replace />} />
          <Route path="users" element={<Navigate to="/sales/users" replace />} />
          <Route path="google-sheet-sync" element={<Navigate to="/sales/google-sheet-sync" replace />} />
          <Route path="caller-groups" element={<Navigate to="/sales/caller-groups" replace />} />
          <Route path="lead-groups" element={<Navigate to="/sales/lead-groups" replace />} />
        </Route>
      </Routes>
    </BrowserRouter>
  );
}

export default App;
